import { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { isAnnotatorRole } from '@/types/users'
import { notifyAnnotatorBlocked } from '@/service/annotatorAccess'
import { Download, RefreshCw, Pencil, Pen, Trash2, Upload, MonitorPlay } from 'lucide-react'
import { useExplainAsVideoEnabled, canExplainAsVideo } from '@/features/videoBrief/explainAsVideo'
import { toast } from 'sonner'
import AppSidebar from '@/components/AppSidebar'
import DataSidebar from '@/components/DataSidebar'
import { useAppStore } from '@/store'
import { useTranslation } from '@/i18n'
import { useApiFeatureFlag } from '@/hooks/useFeatureFlag'
import { useSessionState, DATA_CATALOG_STORAGE_KEYS } from '@/hooks/useSessionState'
import {
  useDocumentStatusStream,
  type DocumentStatusEvent,
} from '@/hooks/useDocumentStatusStream'
import { apiService } from '@/service/api'
import { trackEvent } from '@/utils/analytics'
import { graphEditorUrlForDocument } from '@/utils/resolveGraphOpen'
import { sldOpenGraphId } from '@/routes/GraphEditor/sldGraphViews'
import { buildGraphDocStatusUpdate } from '@/utils/documentStatus'
import type { CatalogStats } from '@/utils/catalogStats'
import { DOC_TYPES, type RowAction } from '@/components/DataTable/DataTableTypes'
import AddDataModal from './DataCatalogV2/AddDataModal'
import {
  AlertDialog, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { IntuiAlertDialogAction } from '@/components/intueri/IntuiAlertDialog'
import MergeAsSetModal from './DataCatalogV2/MergeAsSetModal'

import type { DocumentRecord } from '@/types/catalog'
import { buildNewBoardPositions } from '@/routes/IntelligentBoard/utils/buildNewBoardPositions'
import { batchQueryDocs } from '@/utils/batchQueryDocs'
import { resolveSelectedDocs, unloadedSelectedIds } from './selectedDocResolution'
import StatsBanner from './DataCatalogV2/StatsBanner'
import CatalogFilterBar, { DATE_RANGE_DAYS } from './DataCatalogV2/CatalogFilterBar'
import { buildUploaderOptions } from './DataCatalogV2/uploaderOptions'
import { canMatchEntitySelection, resolveSelectedDocTypes } from './matchEntityEligibility'
import CatalogTableV2, { type SetGroup } from './DataCatalogV2/CatalogTableV2'
import { useFeatureEnabled } from '@/features/featureAccess/useFeatureAccess'
// useDocumentStats removed — stats now derived from local documents state

// UI-65 (Pegah): "We don't need 'P&ID Set' do we? If not remove it" — sets are
// detected automatically now, so the option had nothing left to select. The
// stored filter is validated against this list, so a session that still holds
// `pnid_set` falls back to All rather than to a request nothing answers.
const FILTER_TYPES = [
  'all',
  ...Object.values(DOC_TYPES).filter(
    (type) => type !== 'interview' && type !== 'graph' && type !== 'datasheet'
  ),
]
const INFINITE_SCROLL_PAGE_SIZE = 30

export default function DataCatalogV2() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const getDocTypeLabel = (type: string) => t(`data_catalog:doc_types.${type}` as never)

  const {
    documents, totalDocuments, documentStats, fetchDocuments, updateDocument, deleteDocuments,
    loading, user, generateDefaultBoardName, loadBoardList,
  } = useAppStore()

  const isInternalUser =
    (user?.email?.toLowerCase().endsWith('@intuigence.ai') ||
      user?.email?.toLowerCase().endsWith('@intuigence.com')) ?? false
  // Annotators are read-only outside the P&ID editor — no bulk writes/exports.
  const isAnnotator = isAnnotatorRole(user?.role)

  const { enabled: restrictUpload, loading: restrictUploadLoading } =
    useApiFeatureFlag('restrict_upload_to_internal_users', false)
  // An SLD opens on its parse (VISN-304) only while SLD parsing is on.
  const { enabled: sldParsingEnabled } = useApiFeatureFlag('sld_parsing_enabled', false)
  // "Explain as video" row action (PRD: Explain as video); the server gates the request too.
  const explainVideoEnabled = useExplainAsVideoEnabled()
  // Upload is enabled when: flag is off (all users allowed) OR user is internal
  // While loading, fall back to isInternalUser (matches V1).
  // Annotators are read-only outside the P&ID editor — never allow upload.
  const isUploadEnabled =
    !isAnnotatorRole(user?.role) &&
    (restrictUploadLoading ? isInternalUser : !restrictUpload || isInternalUser)

  // Filter state (session-persisted)
  const [selectedType, setSelectedType] = useSessionState(
    DATA_CATALOG_STORAGE_KEYS.type, 'all',
    (v) => typeof v === 'string' && FILTER_TYPES.includes(v as string)
  )
  const [selectedUploader, setSelectedUploader] = useSessionState(
    DATA_CATALOG_STORAGE_KEYS.uploader, 'all', (v) => typeof v === 'string'
  )
  // V2 consolidated-filter additions (Figma 13331:47890). Status maps to
  // DocumentStatus enum keys ('processing'/'success'/'failed'/'all').
  // creationDate is a relative-range token ('1d'|'3d'|'1w'|'1m'|'3m'|'6m'|'1y'|'all')
  // converted to a cutoff Date below.
  const [selectedStatus, setSelectedStatus] = useSessionState(
    DATA_CATALOG_STORAGE_KEYS.status, 'all', (v) => typeof v === 'string'
  )
  const [selectedCreationDate, setSelectedCreationDate] = useSessionState(
    DATA_CATALOG_STORAGE_KEYS.creationDate, 'all', (v) => typeof v === 'string'
  )
  const [searchTerm, setSearchTerm] = useSessionState(
    DATA_CATALOG_STORAGE_KEYS.search, '', (v) => typeof v === 'string'
  )
  const [sortBy, setSortBy] = useSessionState(
    DATA_CATALOG_STORAGE_KEYS.sortBy, 'created_at', (v) => typeof v === 'string'
  )
  const [sortDir, setSortDir] = useSessionState<'asc' | 'desc'>(
    DATA_CATALOG_STORAGE_KEYS.sortDir, 'desc', (v) => v === 'asc' || v === 'desc'
  )
  // Infinite scroll page cursor (not session-persisted — always start from page 0)
  const pageRef = useRef(0)
  const [loadingMore, setLoadingMore] = useState(false)
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm)
  // Selection is session-persisted as a string[] (a Set doesn't survive
  // JSON.stringify) and exposed below as a Set to keep call sites unchanged.
  const [selectedDocIdsArr, setSelectedDocIdsArr] = useSessionState<string[]>(
    DATA_CATALOG_STORAGE_KEYS.selection, [],
    (v) => Array.isArray(v) && v.every((x) => typeof x === 'string')
  )
  const selectedDocIds = useMemo(() => new Set(selectedDocIdsArr), [selectedDocIdsArr])
  const setSelectedDocIds = useCallback(
    (next: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      setSelectedDocIdsArr((prev) => {
        const resolved =
          typeof next === 'function' ? next(new Set(prev)) : next
        return Array.from(resolved)
      })
    },
    [setSelectedDocIdsArr]
  )
  // Remember each selected doc's type at selection time. The list is
  // server-filtered + paginated, so a selected doc can drop out of `documents`
  // when a filter hides it; this lets selection-gated actions still resolve its
  // type. Not pruned on deselect — stale entries are ignored since resolution
  // only reads currently-selected ids.
  const [selectedTypeById, setSelectedTypeById] = useSessionState<Record<string, DocumentRecord['type']>>(
    DATA_CATALOG_STORAGE_KEYS.selectionTypes,
    {},
    (v) => typeof v === 'object' && v !== null && !Array.isArray(v)
  )
  const captureSelectedTypes = useCallback(
    (docs: ReadonlyArray<Pick<DocumentRecord, 'id' | 'type'>>) => {
      if (docs.length === 0) return
      setSelectedTypeById((prev) => {
        const next = { ...prev }
        for (const doc of docs) next[doc.id] = doc.type
        return next
      })
    },
    [setSelectedTypeById]
  )
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)
  const [showMergeModal, setShowMergeModal] = useState(false)
  // "Open in new board" is in flight — see handleOpenInNewBoard for why both a
  // ref (the guard) and state (the button's spinner) are needed.
  const openingBoardRef = useRef(false)
  const [openingBoard, setOpeningBoard] = useState(false)
  const [userIdToNameMap, setUserIdToNameMap] = useState<Map<string, string>>(new Map())
  // statsRefresh value is unread — only the setter is consumed as a refresh trigger.
  const [_statsRefresh, setStatsRefresh] = useState(0)
  const [showAddDataModal, setShowAddDataModal] = useState(false)
  const [pageDroppedFiles, setPageDroppedFiles] = useState<File[]>([])
  const [isPageDragOver, setIsPageDragOver] = useState(false)
  const pageDragCountRef = useRef(0)

  // Refs for SSE handler (avoid stale closures — matches V1 pattern exactly)
  const documentsRef = useRef<DocumentRecord[]>(documents)
  const documentCompletionRef = useRef<
    Map<string, { indexing: boolean; embedding: boolean }>
  >(new Map())
  useEffect(() => { documentsRef.current = documents }, [documents])

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Fetch documents (reset to page 0 when filters/sort/search change).
  // `created_after` / `status_bucket` come from the V2 cascade filter
  // (Figma 13331:47890). The relative date key (e.g. '1m') is resolved
  // to an ISO instant on every render — fine since it's only consumed
  // by the effect below; the server-side filter then narrows the page
  // before pagination so "load more" stays consistent.
  const createdAfterIso = useMemo(() => {
    const days = DATE_RANGE_DAYS[selectedCreationDate]
    if (!days) return undefined
    return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()
  }, [selectedCreationDate])
  const statusBucket =
    selectedStatus === 'success' || selectedStatus === 'failed' || selectedStatus === 'processing'
      ? selectedStatus
      : undefined
  useEffect(() => {
    pageRef.current = 0
    fetchDocuments({
      page_number: 0,
      page_size: INFINITE_SCROLL_PAGE_SIZE,
      sort_by: sortBy === 'created_at' ? 'upload_started_at' : sortBy,
      sort_direction: sortDir,
      search: debouncedSearch || undefined,
      type: selectedType !== 'all' ? selectedType : undefined,
      user_id: selectedUploader !== 'all' ? selectedUploader : undefined,
      created_after: createdAfterIso,
      status_bucket: statusBucket,
    })
  }, [sortBy, sortDir, debouncedSearch, selectedType, selectedUploader, createdAfterIso, statusBucket, fetchDocuments])

  const hasMore = documents.length < totalDocuments

  // FS-567: a zero-row table under an active search/filter means "no
  // matches", not "no data yet" — drive the table's empty state off this.
  // Uses debouncedSearch (not searchTerm) so it matches what was fetched.
  const hasActiveFilters =
    debouncedSearch !== '' ||
    selectedType !== 'all' ||
    selectedStatus !== 'all' ||
    selectedUploader !== 'all' ||
    selectedCreationDate !== 'all'

  // Unlike the filter bar's "Clear All Filters" (chips only), the empty
  // state's clear also resets search — any of them can cause zero matches.
  // debouncedSearch is reset synchronously too, otherwise the no-matches
  // state lingers for the 300ms debounce window after the input clears
  // (the debounce timer's later set of the same '' is a no-op).
  const handleClearAllFilters = useCallback(() => {
    setSearchTerm('')
    setDebouncedSearch('')
    setSelectedType('all')
    setSelectedStatus('all')
    setSelectedUploader('all')
    setSelectedCreationDate('all')
  }, [setSearchTerm, setSelectedType, setSelectedStatus, setSelectedUploader, setSelectedCreationDate])

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return
    const nextPage = pageRef.current + 1
    pageRef.current = nextPage
    setLoadingMore(true)
    fetchDocuments({
      page_number: nextPage,
      page_size: INFINITE_SCROLL_PAGE_SIZE,
      sort_by: sortBy === 'created_at' ? 'upload_started_at' : sortBy,
      sort_direction: sortDir,
      search: debouncedSearch || undefined,
      type: selectedType !== 'all' ? selectedType : undefined,
      user_id: selectedUploader !== 'all' ? selectedUploader : undefined,
      created_after: createdAfterIso,
      status_bucket: statusBucket,
      append: true,
    }).finally(() => setLoadingMore(false))
  }, [loading, loadingMore, hasMore, sortBy, sortDir, debouncedSearch, selectedType, selectedUploader, createdAfterIso, statusBucket, fetchDocuments])

  // Fetch organization users
  useEffect(() => {
    let mounted = true
    apiService.getOrganizationUsers().then((res) => {
      if (!mounted || !res.users) return
      const mapping = new Map<string, string>()
      res.users.forEach((u) => {
        if (u.user_id && u.name) mapping.set(u.user_id, u.name)
        else if (u.user_id && u.email) mapping.set(u.user_id, u.email)
      })
      setUserIdToNameMap(mapping)
    }).catch(() => {})
    return () => { mounted = false }
  }, [])

  // --- SSE for live status updates (ported from V1 exactly) ---
  // Determine if SSE should be active based on document processing state
  const hasProcessingDocuments = useMemo(() => {
    if (!documents || documents.length === 0) return false
    return documents.some((doc) => {
      const status = doc.status?.toLowerCase() || ''
      if (doc.type === 'document') {
        if (status === 'success' || status === 'failed') return false
        const allComplete =
          doc.processing_metadata?.completed_indexing === true &&
          doc.processing_metadata?.completed_embedding === true
        return !allComplete || (status !== 'success' && status !== 'failed')
      } else if (doc.type === 'device_config') {
        if (status === 'success' || status === 'failed') return false
        const allComplete =
          doc.processing_metadata?.completed_indexing === true &&
          doc.processing_metadata?.completed_embedding === true
        return !allComplete || (status !== 'success' && status !== 'failed')
      } else {
        return !doc.is_indexed || (status !== '' && status !== 'success' && status !== 'failed')
      }
    })
  }, [documents])

  useDocumentStatusStream({
    enabled: hasProcessingDocuments,
    onDisconnected: useCallback(() => {
      fetchDocuments()
    }, [fetchDocuments]),
    onStatusUpdate: useCallback(
      (event: DocumentStatusEvent) => {
        const doc = documentsRef.current?.find((d) => d.id === event.document_id)

        // KG regeneration is best effort after ingestion. Read its persisted
        // result instead of overriding the backend's success/failure guards.
        if (event.event_type.startsWith('kg_regen_')) {
          fetchDocuments()
          return
        }

        // Handle failure events first
        if (
          event.event_type === 'indexing_failed' ||
          event.event_type === 'embedding_failed'
        ) {
          documentCompletionRef.current.delete(event.document_id)
          trackEvent('document-processing_failed', {
            document_id: event.document_id,
            document_type: doc?.type || 'unknown',
            error_type: event.event_type,
          })
          updateDocument(event.document_id, {
            status: 'failed',
            is_indexed: doc?.is_indexed || event.is_indexed,
          })
          setStatsRefresh((n) => n + 1)
          return
        }

        // Handle re-processing: reset completion state
        if (event.event_type === 'indexing_started') {
          documentCompletionRef.current.delete(event.document_id)
          setStatsRefresh((n) => n + 1)
        }

        if (doc?.type === 'document') {
          let completion = documentCompletionRef.current.get(event.document_id)
          if (!completion) {
            completion = {
              indexing: doc.processing_metadata?.completed_indexing === true,
              embedding: doc.processing_metadata?.completed_embedding === true,
            }
            documentCompletionRef.current.set(event.document_id, completion)
          }
          if (event.event_type === 'indexing_completed') completion.indexing = true
          else if (event.event_type === 'embedding_completed') {
            completion.embedding = true
            setTimeout(() => fetchDocuments(), 1000)
          } else if (event.event_type === 'processing_complete') {
            completion.indexing = true
            completion.embedding = true
          }
          const allComplete = completion.indexing && completion.embedding
          updateDocument(event.document_id, {
            status: allComplete ? 'success' : 'processing',
            is_indexed: doc?.is_indexed || event.is_indexed,
            processing_metadata: {
              completed_indexing: completion.indexing,
              completed_embedding: completion.embedding,
            },
          })
          setStatsRefresh((n) => n + 1)
          if (allComplete) {
            trackEvent('document-processing_completed', {
              document_id: event.document_id,
              document_type: doc?.type || 'document',
            })
            setTimeout(() => documentCompletionRef.current.delete(event.document_id), 5000)
            setTimeout(() => { fetchDocuments(); setStatsRefresh((n) => n + 1) }, 100)
          }
        } else if (doc?.type === 'device_config') {
          let completion = documentCompletionRef.current.get(event.document_id)
          if (!completion) {
            completion = {
              indexing: doc.processing_metadata?.completed_indexing === true,
              embedding: doc.processing_metadata?.completed_embedding === true,
            }
            documentCompletionRef.current.set(event.document_id, completion)
          }
          if (event.event_type === 'indexing_completed') completion.indexing = true
          else if (event.event_type === 'embedding_completed') completion.embedding = true
          else if (event.event_type === 'processing_complete') {
            completion.indexing = true
            completion.embedding = true
          }
          const allComplete = completion.indexing && completion.embedding
          updateDocument(event.document_id, {
            status: allComplete ? 'success' : 'processing',
            is_indexed: doc?.is_indexed || event.is_indexed,
            processing_metadata: {
              completed_indexing: completion.indexing,
              completed_embedding: completion.embedding,
            },
          })
          setStatsRefresh((n) => n + 1)
          if (allComplete) {
            trackEvent('document-processing_completed', {
              document_id: event.document_id,
              document_type: doc?.type || 'device_config',
            })
            setTimeout(() => documentCompletionRef.current.delete(event.document_id), 5000)
            setTimeout(() => { fetchDocuments(); setStatsRefresh((n) => n + 1) }, 100)
          }
        } else {
          // Other types (incl. P&ID/PFD): direct status update. FS-591 — the
          // helper also merges the freshly-created graph_id from the completion
          // event's metadata into properties so the row is immediately openable
          // from live state (no manual page refresh needed).
          updateDocument(event.document_id, buildGraphDocStatusUpdate(doc, event))
          setStatsRefresh((n) => n + 1)
        }

        // Refetch when processing creates new server-side data (graph_id, row_count).
        //
        // FS-601: `processing_complete` is in this list deliberately. The
        // per-page `*_processing_completed` events fire BEFORE the worker writes
        // is_indexed/status='success' (pnid_worker.py:1196 vs :1320), so a
        // refetch keyed only on those always reads a pre-completion snapshot and
        // never converges. `processing_complete` is the first event emitted
        // after those writes land, so it is the one that reads the finished row.
        if (
          event.event_type === 'pnid_processing_completed' ||
          event.event_type === 'pfd_processing_completed' ||
          event.event_type === 'datasheet_processing_completed' ||
          event.event_type === 'timeseries_processing_completed' ||
          event.event_type === 'processing_complete'
        ) {
          setTimeout(() => { fetchDocuments(); setStatsRefresh((n) => n + 1) }, 100)
        }
      },
      // IMPORTANT: Do not add documents to deps — accessed via ref to prevent SSE churn
      [updateDocument, fetchDocuments]
    ),
  })

  // There is deliberately NO periodic refetch here. Status arrives over SSE
  // above, which patches the row and delta-syncs the header counts; when SSE is
  // genuinely down, DocumentStatusStreamManager's own fallback poll fires
  // onDisconnected every 30s and that handler refetches. A second interval on
  // top of those was gated on the same `hasProcessingDocuments` as the stream,
  // so it could only run while SSE was already connected and working — it
  // refetched the whole loaded window every 20s and found nothing changed.
  // (Measured on dev over 24h: 27/27 streams closed by the client, zero server
  // or upstream drops, zero failed connects, p50 connection life ~20 min.)

  // Stats from backend (aggregated across ALL documents, not just the loaded page)
  const stats: CatalogStats = useMemo(() => {
    if (documentStats) {
      return {
        totalDocuments,
        totalSizeBytes: documentStats.total_size_bytes,
        processedCount: documentStats.processed_count,
        processingCount: documentStats.processing_count,
        failedCount: documentStats.failed_count,
      }
    }
    return { totalDocuments, totalSizeBytes: 0, processedCount: 0, processingCount: 0, failedCount: 0 }
  }, [totalDocuments, documentStats])

  // Enrich documents with uploader names. Status + creation-date
  // filtering is handled server-side (PaginationDto.status_bucket /
  // .created_after) so the list here is already narrowed before
  // pagination — no extra client-side pass needed.
  const enrichedDocs = useMemo(() =>
    documents.map((doc) => ({
      ...doc,
      uploader_name: doc.uploader_name || userIdToNameMap.get(doc.user_id) || null,
    })),
    [documents, userIdToNameMap]
  )

  // Unique uploaders — sourced from the organization directory, not the loaded
  // page slice. See uploaderOptions.ts for why (UI-63).
  const uniqueUploaders = useMemo(
    () => buildUploaderOptions(userIdToNameMap, documents),
    [documents, userIdToNameMap]
  )


  // --- Row click handler (ported from V1) ---
  const handleRowClick = useCallback((doc: DocumentRecord) => {
    if (doc.status === 'uploading') return
    if (doc.status === 'failed' || doc.status === 'error') {
      toast.error(doc.error_message || t('data_catalog:file_processing_failed', 'File processing failed.'))
      return
    }
    // Annotators may only open diagrams — P&IDs, PFDs and SLDs, all of which
    // open the graph editor, their annotation surface. Every other doc type —
    // timeseries, datasheets, documents, transcripts — is view-blocked. The
    // /document, /datasheet, /timeseries, /interview, /user-artifact routes are
    // guarded too (AnnotatorRestrictedRoute) so deep links can't bypass this.
    //
    // SLD is here because annotating them is the whole reason the role exists
    // for this corpus: leaving it out blocks an intern from the only drawings
    // they were invited to work on.
    if (
      isAnnotator &&
      doc.type !== DOC_TYPES.pnid &&
      doc.type !== DOC_TYPES.pfd &&
      doc.type !== DOC_TYPES.sld
    ) {
      notifyAnnotatorBlocked()
      return
    }
    const ext = doc.name?.split('.').pop()?.toLowerCase()
    const isSpreadsheetWithDb = doc.type === 'document' && ['xlsx', 'xls', 'csv'].includes(ext || '') && doc.properties?.db_path
    // Multi-page P&IDs (per-page graphs in pnid_graph_ids) require is_indexed
    // before opening — parity with the V1 catalog.
    const isMultiPagePnid =
      doc.type === 'pnid' &&
      Boolean((doc.properties as { pnid_graph_ids?: unknown } | undefined)?.pnid_graph_ids)
    // A live_stream anchor (Eventhouse / streamed tags) is deliberately
    // is_indexed=false — there is no file to index; its metadata + tags come
    // from the harvest. Gating on is_indexed made double-click dead-end on
    // "File is still being processed..." forever.
    const isLiveStream = doc.type === 'live_stream'
    // An SLD is deliberately is_indexed=false, for the same reason a live_stream
    // is: there is no parsed content to index. Its readiness is `status`, set
    // when its pages are registered. Gating on is_indexed here would dead-end
    // every SLD on "File is still being processed..." forever.
    const isSld = doc.type === DOC_TYPES.sld
    // A video brief is generated, never indexed; its readiness is `status`.
    const isVideo = doc.type === DOC_TYPES.video
    if (
      (doc.type !== 'pnid' || isMultiPagePnid) &&
      !isSpreadsheetWithDb &&
      !isLiveStream &&
      !isSld &&
      !isVideo &&
      !doc.is_indexed
    ) {
      toast.info(t('data_catalog:file_still_processing', 'File is still being processed...'))
      return
    }
    switch (doc.type) {
      // All three diagram types open the graph editor. An SLD's per-page graphs
      // live in `pnid_graph_ids` / `graph_id` exactly as a P&ID's do — written
      // by `SldService` instead of a parse worker, so they resolve identically.
      case DOC_TYPES.pnid:
      case DOC_TYPES.pfd:
      case DOC_TYPES.sld: {
        // Resolves both shapes: single-page graph_id and multi-page
        // pnid_graph_ids (page→graph map; graph_id is unset for those).
        // An SLD opens on its parse when there is one; people open it to see that.
        const url = graphEditorUrlForDocument(
          doc.id,
          doc.properties,
          isSld && sldParsingEnabled ? (graphId) => sldOpenGraphId(doc, graphId) : undefined,
        )
        if (url) return navigate(url)
        // No graph resolved. For an SLD this is reachable on the NORMAL path:
        // the optimistic row prepended right after upload carries no graph_id
        // yet, and it is permanent whenever page registration failed. Falling
        // through to `return` made the click a silent no-op — indistinguishable
        // from a dead UI. Say what is happening instead.
        if (isSld) {
          toast.info(t('data_catalog:file_still_processing', 'File is still being processed...'))
        }
        return
      }
      case DOC_TYPES.document:
        if (['xlsx', 'xls', 'csv'].includes(ext || '') && doc.properties?.db_path) return navigate(`/datasheet/${doc.id}`)
        return navigate(`/document/${doc.id}`)
      case DOC_TYPES.table:
        return navigate(`/datasheet/${doc.id}`)
      case DOC_TYPES.timeseries:
        if (doc.properties?.db_path) return navigate(`/datasheet/${doc.id}`)
        if (doc.name?.toLowerCase().endsWith('.json')) return navigate(`/timeseries/${doc.id}`)
        return navigate(`/document/${doc.id}`)
      // Live streams have no file behind them, so the .json / db_path
      // discriminators above don't apply. Its own viewer (LiveStreamPreview)
      // shows where the stream points, whether it is live, when the tag
      // inventory was last refreshed and which tags it carries — from the
      // document + connection rows only, so opening it costs no Kusto query.
      case 'live_stream':
        return navigate(`/live-stream/${doc.id}`)
      case DOC_TYPES.plc_program:
        // Parsed logic viewer loading the worker's stored artifact (VISN-305).
        return navigate(`/plc-parser/${doc.id}`)
      case DOC_TYPES.interview:
        return navigate(`/interview/${doc.id}`)
      case DOC_TYPES.transcript:
        // FS-557 "User Artifacts" — call-transcript viewer/editor.
        return navigate(`/user-artifact/${doc.id}`)
      case DOC_TYPES.video:
        // The brief is playable once the worker stamps `success`; before that the
        // row exists only so the catalog can show it rendering.
        if (doc.status !== 'success') {
          toast.info(t('data_catalog:file_still_processing', 'File is still being processed...'))
          return
        }
        return navigate(`/video/${doc.id}`)
      default:
        return
    }
  }, [navigate, t, isAnnotator, sldParsingEnabled])

  // --- Row action handlers ---
  const handleDownload = useCallback(async (docId: string) => {
    const doc = documents.find((d) => d.id === docId)
    if (!doc) return
    // Every document type downloads the original uploaded file. A parsed P&ID
    // briefly downloaded the editor's annotated PNG instead; that lost the only
    // way to get the source drawing out of the catalog, so it was reverted. The
    // annotated image stays available from the editor's own Download button.
    try {
      const blob = await apiService.downloadDocument(docId)
      let downloadName = doc.name
      if (doc.location?.toLowerCase().endsWith('.pdf') && !doc.name.toLowerCase().endsWith('.pdf')) {
        const base = doc.name.includes('.') ? doc.name.substring(0, doc.name.lastIndexOf('.')) : doc.name
        downloadName = `${base}.pdf`
      }
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = downloadName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      trackEvent('document-downloaded', { document_id: doc.id, document_type: doc.type })
    } catch {
      toast.error(t('data_catalog:download_error'))
    }
  }, [documents, t])

  const handleDeleteDoc = useCallback(async (id: string) => {
    try {
      await deleteDocuments([id])
      trackEvent('document-deleted', { document_ids: [id], count: 1, is_bulk: false })
      toast.success(t('data_catalog:delete_success'))
      setStatsRefresh((n) => n + 1)
    } catch {
      toast.error(t('data_catalog:delete_error'))
    }
  }, [deleteDocuments, t])

  const handleRetryProcessing = useCallback(async (docId: string) => {
    try {
      await apiService.retryDocumentProcessing(docId)
      toast.success(t('data_catalog:retry_processing_success'))
      await fetchDocuments()
    } catch {
      toast.error(t('data_catalog:retry_processing_error'))
    }
  }, [fetchDocuments, t])

  // Files with a brief request in flight: a second click while the menu is
  // still open would create a second row and a second render.
  const videoRequestsRef = useRef<Set<string>>(new Set())
  const handleExplainAsVideo = useCallback(async (docId: string) => {
    if (videoRequestsRef.current.has(docId)) return
    videoRequestsRef.current.add(docId)
    try {
      const receipt = await apiService.createVideoBrief({ documentId: docId })
      toast.success(t('data_catalog:video_brief.requested', { name: receipt.name }))
      // The brief's row is already there, processing; show it.
      await fetchDocuments()
    } catch {
      toast.error(t('data_catalog:video_brief.error'))
    } finally {
      videoRequestsRef.current.delete(docId)
    }
  }, [fetchDocuments, t])

  const handleRename = useCallback(async (id: string, newName: string) => {
    try {
      await apiService.renameDocument(id, newName)
      // FS-563: patch just the renamed row in place rather than refetching the
      // whole catalog. A full fetchDocuments() replaces the `documents` array
      // wholesale, which flickers/reloads every row; updateDocument touches only
      // this row so the rest of the table stays put.
      updateDocument(id, { name: newName })
      toast.success(t('data_catalog:rename_success', 'File renamed'))
    } catch {
      toast.error(t('data_catalog:rename_error', 'Failed to rename file'))
    }
  }, [updateDocument, t])

  // --- Bulk actions (delete, group, ungroup) ---
  const canMergeAsSet = useMemo(() => {
    if (selectedDocIds.size < 2) return false
    const selectedDocs = documents.filter((d) => selectedDocIds.has(d.id))
    return (
      selectedDocs.length >= 2 &&
      selectedDocs.every(
        (d) => (d.type === 'pnid' || d.type === 'pfd') && !d.properties.multi_page_set_id
      )
    )
  }, [selectedDocIds, documents])

  const selectedDocsForMerge = useMemo(() => {
    return documents.filter((d) => selectedDocIds.has(d.id))
  }, [selectedDocIds, documents])

  // Build set groups from documents that share a multi_page_set_id
  const setGroups: SetGroup[] = useMemo(() => {
    const groupMap = new Map<string, { name: string; docs: DocumentRecord[] }>()
    for (const doc of enrichedDocs) {
      const setId = doc.properties?.multi_page_set_id
      if (!setId) continue
      if (!groupMap.has(setId)) {
        groupMap.set(setId, {
          name: doc.properties.multi_page_set_name || t('data_catalog:multi_page_set.unnamed_set_default'),
          docs: [],
        })
      }
      groupMap.get(setId)!.docs.push(doc)
    }
    // Sort child docs by sheet_index
    return Array.from(groupMap.entries()).map(([setId, { name, docs }]) => ({
      setId,
      setName: name,
      documents: docs.sort(
        (a, b) => (Number(a.properties.sheet_index) || 0) - (Number(b.properties.sheet_index) || 0)
      ),
    }))
  }, [enrichedDocs, t])

  const handleBulkDelete = useCallback(async () => {
    if (selectedDocIds.size === 0) return
    const ids = Array.from(selectedDocIds)
    try {
      await deleteDocuments(ids)
      trackEvent('document-deleted', { document_ids: ids, count: ids.length, is_bulk: true })
      // Count-aware string, matching V1 — the singular delete_success read
      // "File deleted" after a 218-file bulk delete.
      toast.success(t('data_catalog:bulk_delete.success', { count: ids.length }))
      setSelectedDocIds(new Set())
      setShowBulkDeleteConfirm(false)
      setStatsRefresh((n) => n + 1)
    } catch {
      toast.error(t('data_catalog:delete_error'))
    }
  }, [selectedDocIds, deleteDocuments, setSelectedDocIds, t])

  const handleMergeAsSet = useCallback(async (setName: string, docIds: string[]) => {
    const setId = crypto.randomUUID()
    try {
      await Promise.all(
        docIds.map((docId, index) =>
          apiService.updateDocumentMetadata(docId, {
            multi_page_set_id: setId,
            sheet_index: String(index),
            multi_page_set_name: setName,
          })
        )
      )
      await fetchDocuments()
      setSelectedDocIds(new Set())
      setShowMergeModal(false)
      toast.success(t('data_catalog:graph_editor.multi_page_set.group_success'))
    } catch {
      toast.error(t('data_catalog:graph_editor.multi_page_set.group_error'))
    }
  }, [fetchDocuments, setSelectedDocIds, t])

  // Ungroup a multi-page set (remove multi_page_set_id from all its docs)
  const handleUngroupSet = useCallback(async (setId: string) => {
    const group = setGroups.find((g) => g.setId === setId)
    if (!group) return
    try {
      await Promise.all(
        group.documents.map((doc) =>
          apiService.updateDocumentMetadata(doc.id, {}, [
            'multi_page_set_id',
            'sheet_index',
            'multi_page_set_name',
          ])
        )
      )
      await fetchDocuments()
      toast.success(t('data_catalog:graph_editor.multi_page_set.ungroup_success', 'Set ungrouped'))
    } catch {
      toast.error(t('data_catalog:graph_editor.multi_page_set.ungroup_error', 'Failed to ungroup'))
    }
  }, [setGroups, fetchDocuments, t])

  // Check if any selected docs belong to a group (for bulk ungroup)
  const canUngroupSelected = useMemo(() => {
    if (selectedDocIds.size === 0) return false
    const selectedDocs = documents.filter((d) => selectedDocIds.has(d.id))
    return selectedDocs.some((d) => d.properties?.multi_page_set_id)
  }, [selectedDocIds, documents])

  // Match Entity runs tag-mapping actions, so it goes with that area.
  const tagMappingEnabled = useFeatureEnabled('tag_mapping')
  const canMatchEntity = useMemo(
    () =>
      tagMappingEnabled &&
      canMatchEntitySelection(
        resolveSelectedDocTypes(selectedDocIds, documents, selectedTypeById)
      ),
    [tagMappingEnabled, selectedDocIds, documents, selectedTypeById]
  )

  const handleBulkUngroup = useCallback(async () => {
    const selectedGroupedDocs = documents.filter(
      (d) => selectedDocIds.has(d.id) && d.properties?.multi_page_set_id
    )
    if (selectedGroupedDocs.length === 0) return
    try {
      await Promise.all(
        selectedGroupedDocs.map((doc) =>
          apiService.updateDocumentMetadata(doc.id, {}, [
            'multi_page_set_id',
            'sheet_index',
            'multi_page_set_name',
          ])
        )
      )
      await fetchDocuments()
      setSelectedDocIds(new Set())
      toast.success(t('data_catalog:graph_editor.multi_page_set.ungroup_success', 'Set ungrouped'))
    } catch {
      toast.error(t('data_catalog:graph_editor.multi_page_set.ungroup_error', 'Failed to ungroup'))
    }
  }, [selectedDocIds, documents, fetchDocuments, setSelectedDocIds, t])

  // Open a new board prefilled with the selected documents.
  // Lays them out on the same grid the board canvas uses, then navigates.
  const handleOpenInNewBoard = useCallback(async () => {
    // Annotators are read-only — "open in new board" creates a board (blocked).
    if (isAnnotatorRole(user?.role)) return notifyAnnotatorBlocked()
    if (selectedDocIds.size === 0) return
    // Hydration + creation are two round-trips, so a second click lands while
    // the first is still in flight and creates a DUPLICATE board. The ref is
    // what actually guards (a state read would still be the pre-click value);
    // the state drives the button's spinner/disabled.
    if (openingBoardRef.current) return
    openingBoardRef.current = true
    setOpeningBoard(true)
    const selectedIds = Array.from(selectedDocIds)

    try {
      // FS-625: the selection survives a filter change but the loaded rows do
      // not — switching the Document Type filter refetches and replaces them.
      // Reading the selection out of `documents` alone therefore dropped every
      // file selected under an earlier filter (select P&IDs, switch to Time
      // Series, open a board → only the timeseries landed), so hydrate the ids
      // this page no longer holds and give the board what the button counted.
      const missingIds = unloadedSelectedIds(selectedIds, documents)
      const hydratedDocs = missingIds.length > 0 ? await batchQueryDocs(missingIds) : []
      const selectedDocs = resolveSelectedDocs(selectedIds, documents, hydratedDocs)
      // An id the server doesn't return either (deleted from another tab/session)
      // can't go on a board. Dropping it silently is the exact failure this whole
      // fix is about — a board that holds fewer files than the button counted —
      // so say so. Creating the board with what remains still beats refusing.
      const unavailableCount = selectedIds.length - selectedDocs.length
      const warnUnavailable = () =>
        toast.warning(
          t('data_catalog:open_in_new_board.partial', {
            defaultValue:
              "{{missing}} of {{total}} selected files are no longer available and weren't added",
            missing: unavailableCount,
            total: selectedIds.length,
          })
        )
      if (selectedDocs.length === 0) {
        // Every id is gone. Keeping them selected would leave a count that
        // stands for nothing and re-warn on every click, so drop them — the
        // query SUCCEEDED and returned none of them, which is different from
        // the catch below (a failed lookup keeps the selection to retry).
        warnUnavailable()
        setSelectedDocIds(new Set())
        return
      }

      // FS-556: buildNewBoardPositions resolves each processed P&ID/PFD to its
      // parsed graph (graph_id + doc_type 'graph') so the new board renders the
      // diagram, matching the in-board "Add files" modal and master-board sync.
      // Previously this persisted doc.id + raw doc.type, so a P&ID came through
      // as a document.
      const positions = buildNewBoardPositions(selectedDocs)

      // Ensure the board name doesn't collide with existing Untitled Board N.
      await loadBoardList()
      const name = generateDefaultBoardName()
      const board = await apiService.createWorkspace(name, positions)
      trackEvent('workspace-created', {
        workspace_id: board.id,
      })
      if (unavailableCount > 0) warnUnavailable()
      setSelectedDocIds(new Set())
      navigate(`/board/${board.id}`)
    } catch {
      toast.error(t('data_catalog:open_in_new_board.error', { defaultValue: 'Failed to open new board' }))
    } finally {
      openingBoardRef.current = false
      setOpeningBoard(false)
    }
  }, [selectedDocIds, documents, loadBoardList, generateDefaultBoardName, navigate, setSelectedDocIds, t, user])

  // --- Row actions menu ---
  const menuActions: RowAction[] = useMemo(() => {
    const all: RowAction[] = [
    {
      name: t('data_catalog:row_actions.download'),
      icon: <Download className="w-4 h-4" />,
      action: (_e, id) => { handleDownload(id) },
      condition: (doc: DocumentRecord) => doc.status !== 'uploading',
    },
    {
      name: t('data_catalog:row_actions.retry_processing'),
      icon: <RefreshCw className="w-4 h-4" />,
      action: (_e, id) => { handleRetryProcessing(id) },
      // FS-621: also offer retry for the reconciler's 'stalled' state, not just
      // 'failed' — the backend retry gate accepts both.
      condition: (doc: DocumentRecord) => doc.status === 'failed' || doc.status === 'stalled',
    },
    {
      name: t('data_catalog:row_actions.rename'),
      icon: <Pencil className="w-4 h-4" />,
      isRename: true,
      action: () => {},
      condition: (doc: DocumentRecord) => doc.status !== 'uploading',
    },
    // After Rename, before Delete: the PRD's placement (PRD: Explain as video).
    ...(explainVideoEnabled
      ? [
          {
            name: t('data_catalog:row_actions.explain_as_video'),
            icon: <MonitorPlay className="w-4 h-4" />,
            action: (_e, id) => { handleExplainAsVideo(id) },
            condition: canExplainAsVideo,
          } satisfies RowAction,
        ]
      : []),
    {
      name: t('data_catalog:row_actions.open_in_tl_draw'),
      file_type: [DOC_TYPES.graph],
      icon: <Pen className="w-4 h-4" />,
      action: (_e, id) => { navigate(`/graph-editor/${id}`) },
      condition: (doc: DocumentRecord) => doc.status !== 'uploading',
    },
    {
      name: t('data_catalog:row_actions.delete'),
      icon: <Trash2 className="w-4 h-4" />,
      action: (_e, id) => { handleDeleteDoc(id) },
    },
    ]
    // Annotators are read-only: the only row action is opening the P&ID to
    // annotate — no download (exfil), retry (reparse), rename, or delete.
    if (isAnnotatorRole(user?.role)) {
      return all.filter((a) => a.name === t('data_catalog:row_actions.open_in_tl_draw'))
    }
    return all
  }, [t, handleDownload, handleRetryProcessing, handleExplainAsVideo, explainVideoEnabled, handleDeleteDoc, navigate, user])

  const handleUploadClick = () => {
    setShowAddDataModal(true)
  }

  // Page-level drag-and-drop overlay
  const handlePageDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    if (isAnnotator) return // read-only: no upload drop-zone for annotators
    pageDragCountRef.current++
    if (pageDragCountRef.current === 1) setIsPageDragOver(true)
  }, [isAnnotator])
  const handlePageDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    pageDragCountRef.current--
    if (pageDragCountRef.current === 0) setIsPageDragOver(false)
  }, [])
  const handlePageDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
  }, [])
  const handlePageDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    pageDragCountRef.current = 0
    setIsPageDragOver(false)
    // Annotators are read-only: dropped files must not open the upload flow.
    if (isAnnotator) {
      notifyAnnotatorBlocked()
      return
    }
    const droppedFiles = Array.from(e.dataTransfer.files)
    setPageDroppedFiles(droppedFiles)
    setShowAddDataModal(true)
  }, [isAnnotator])

  return (
    <div
      className="flex h-screen relative"
      onDragEnter={handlePageDragEnter}
      onDragLeave={handlePageDragLeave}
      onDragOver={handlePageDragOver}
      onDrop={handlePageDrop}
    >
      <AppSidebar />
      <DataSidebar />

      {/* Content */}
      <div className="flex flex-1 flex-col min-w-0">
        {/* Topbar */}
        <header className="flex h-[60px] shrink-0 items-center justify-between border-b border-[#e5e5e5] bg-white px-6 dark:border-dark-border dark:bg-dark-card">
          <div className="flex items-center">
            <span className="text-sm font-medium text-foreground">
              {/* FS-550: say "Data" to match the left-rail label (not "Data Catalog"). */}
              {/* UI-67: no glyph beside it — see AppTopbar. */}
              {t('nav:menu.data', 'Data')}
            </span>
          </div>
        </header>

        {/* Main content */}
        <main className="flex flex-1 flex-col min-h-0 bg-white dark:bg-dark-card">
          <StatsBanner stats={stats} />
          <CatalogFilterBar
            searchTerm={searchTerm}
            onSearchChange={(v) => { setSearchTerm(v) }}
            selectedType={selectedType}
            onTypeChange={(v) => { setSelectedType(v) }}
            filterTypes={FILTER_TYPES}
            getDocTypeLabel={getDocTypeLabel}
            selectedUploader={selectedUploader}
            onUploaderChange={(v) => { setSelectedUploader(v) }}
            uploaders={uniqueUploaders}
            selectedStatus={selectedStatus}
            onStatusChange={(v) => { setSelectedStatus(v) }}
            selectedCreationDate={selectedCreationDate}
            onCreationDateChange={(v) => { setSelectedCreationDate(v) }}
            onUploadClick={handleUploadClick}
            isUploadEnabled={isUploadEnabled}
            selectedCount={selectedDocIds.size}
            canMergeAsSet={!isAnnotator && canMergeAsSet}
            onMergeAsSet={() => setShowMergeModal(true)}
            canUngroupSelected={!isAnnotator && canUngroupSelected}
            onBulkUngroup={handleBulkUngroup}
            canMatchEntity={!isAnnotator && canMatchEntity}
            onMatchEntity={() => {
              const ids = Array.from(selectedDocIds).join(',')
              navigate(`/data-catalog/match-entity?docs=${encodeURIComponent(ids)}`)
            }}
            onOpenInNewBoard={isAnnotator ? undefined : handleOpenInNewBoard}
            openInNewBoardBusy={openingBoard}
            onBulkDelete={isAnnotator ? undefined : () => setShowBulkDeleteConfirm(true)}
            onClearSelection={() => setSelectedDocIds(new Set())}
          />

          <CatalogTableV2
            data={enrichedDocs}
            loading={loading}
            onRowClick={handleRowClick}
            rowActions={menuActions}
            sort={{ key: sortBy, direction: sortDir }}
            onSortChange={(s) => { setSortBy(s.key); setSortDir(s.direction) }}
            onRename={handleRename}
            getDocTypeLabel={getDocTypeLabel}
            selectedRows={selectedDocIds}
            onRowSelect={(id, checked) => {
              setSelectedDocIds((prev) => {
                const next = new Set(prev)
                if (checked) next.add(id)
                else next.delete(id)
                return next
              })
              if (checked) {
                const doc = documents.find((d) => d.id === id)
                if (doc) captureSelectedTypes([doc])
              }
            }}
            onSelectAll={(checked) => {
              // FS-625: select-all covers the rows the user can SEE — it adds
              // them and unchecking removes them, leaving selections the current
              // filter hides untouched. It used to replace the whole selection
              // with the visible rows, so selecting all P&IDs, filtering to Time
              // Series and selecting those discarded all 30 P&IDs with no signal
              // (the header checkbox already derives its state from the visible
              // rows only, so replacing was inconsistent with the control too).
              // The ✕ button is the deliberate clear-everything escape hatch.
              const selectable = enrichedDocs.filter((d) => d.status !== 'uploading')
              setSelectedDocIds((prev) => {
                const next = new Set(prev)
                for (const doc of selectable) {
                  if (checked) next.add(doc.id)
                  else next.delete(doc.id)
                }
                return next
              })
              if (checked) captureSelectedTypes(selectable)
            }}
            onUploadClick={isUploadEnabled ? handleUploadClick : undefined}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={handleClearAllFilters}
            setGroups={setGroups}
            onSetClick={(group) => {
              // A set is now rendered as soon as its sheets are registered, so
              // it can be clicked while they are still uploading — the viewer
              // would open on sheets that have no bytes behind them yet. Single
              // rows have always been guarded this way in `handleRowClick`; the
              // set parent row never was, because it could not appear this early.
              if (group.documents.some((d) => d.status === 'uploading')) return
              navigate(`/pnid-set/${group.setId}?docIds=${group.documents.map((d) => d.id).join(',')}`)
            }}
            onUngroupSet={handleUngroupSet}
            onLoadMore={loadMore}
            hasMore={hasMore}
            loadingMore={loadingMore}
          />
        </main>
      </div>

      {/* Bulk delete confirmation */}
      {showBulkDeleteConfirm && (
        <AlertDialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('data_catalog:bulk_delete.title', { count: selectedDocIds.size })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('data_catalog:bulk_delete.description')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setShowBulkDeleteConfirm(false)}>
                {t('common:buttons.cancel')}
              </AlertDialogCancel>
              <IntuiAlertDialogAction
                onClick={handleBulkDelete}
                variant="destructive"
              >
                {t('common:buttons.delete')}
              </IntuiAlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/* Merge as Set modal */}
      <MergeAsSetModal
        isOpen={showMergeModal}
        onClose={() => setShowMergeModal(false)}
        documents={selectedDocsForMerge}
        onMerge={handleMergeAsSet}
      />

      {/* Add Data modal */}
      <AddDataModal
        isOpen={showAddDataModal}
        onClose={() => { setShowAddDataModal(false); setPageDroppedFiles([]) }}
        initialFiles={pageDroppedFiles}
      />

      {/* Page-level drag overlay */}
      {isPageDragOver && (
        <div className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none">
          <div className="bg-white dark:bg-dark-card rounded-xl shadow-xl px-10 py-8 flex flex-col items-center gap-3">
            <Upload className="size-6 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{t('data_catalog:dnd.drop_files_to_upload')}</span>
          </div>
        </div>
      )}
    </div>
  )
}
